import { useState } from 'react'
import './index.scss'

import { useAuth } from '@auth/AuthProvider'
import Modal, { ModalButtonStyle } from '@components/Modal'
import { useChurchDataProvider } from '@data/ChurchDataProvider'
import LaunchpadController from '@data/controller/LaunchpadController'
import NetworkController from '@data/controller/NetworkController'
import {
  Launchpad,
  LaunchpadAttributes,
  launchpadFromResponse,
  normalizeLaunchpadAttributes
} from '@interfaces/launchpad.interface'

import closeIcon from 'url:@icon/close.svg';

export default function LaunchpadViewModal(props: {
  isOpen: boolean,
  launchpad?: Launchpad | null,
  close: () => void,
  onSaved: (launchpad: Launchpad) => void
}) {
  const auth = useAuth();
  const churchData = useChurchDataProvider();
  const modalTitle = props.launchpad?.metadata.attributes.name ?? 'New Launchpad View';
  const [name, setName] = useState(props.launchpad?.metadata.attributes.name ?? '');
  const [description, setDescription] = useState(props.launchpad?.metadata.attributes.description ?? '');
  const [campusID, setCampusID] = useState(props.launchpad?.metadata.attributes.campusID ?? '');
  const [categoryID, setCategoryID] = useState(props.launchpad?.metadata.attributes.categoryID ?? '');
  const [isCategoryPickerOpen, setIsCategoryPickerOpen] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const existingAttributes = props.launchpad?.metadata.attributes;
  const existingLogoURL = existingAttributes?.logoURL ?? null;

  const saveLaunchpad = async () => {
    if (!auth.churchId) {
      setError('Missing church context. Please refresh and try again.');
      return;
    }

    const trimmedName = name.trim();

    if (trimmedName.length === 0) {
      setError('Please give this Launchpad a title.');
      return;
    }

    const trimmedDescription = description.trim();

    if (trimmedDescription.length === 0) {
      setError('Please give this Launchpad a description.');
      return;
    }

    if (!campusID) {
      setError('Please select a campus for this Launchpad.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let logoURL = existingLogoURL;

      if (logoFile) {
        const uploadTargetResponse = await LaunchpadController.createAssetUploadTarget(
          auth.churchId,
          logoFile.type || 'application/octet-stream',
          logoFile.size,
          auth
        );

        if (!uploadTargetResponse.body) {
          setError(uploadTargetResponse.error || 'Unable to create an upload target for the logo image.');
          setIsSaving(false);
          return;
        }

        const uploadSucceeded = await NetworkController.performSignedPost(
          {
            url: uploadTargetResponse.body.url,
            fields: uploadTargetResponse.body.fields
          },
          logoFile,
          logoFile.type
        );

        if (!uploadSucceeded) {
          setError('The logo upload failed. Please try again.');
          setIsSaving(false);
          return;
        }

        logoURL = uploadTargetResponse.body.assetURL;
      }

      const attributes: LaunchpadAttributes = normalizeLaunchpadAttributes({
        name: trimmedName,
        description: trimmedDescription,
        logoURL,
        campusID: campusID || null,
        categoryID: categoryID || null,
        links: existingAttributes?.links ?? []
      });

      const response = props.launchpad
        ? await LaunchpadController.updateLaunchpad(
          auth.churchId,
          props.launchpad.launchpadID,
          attributes,
          auth
        )
        : await LaunchpadController.createLaunchpad(
          auth.churchId,
          attributes,
          auth
        );

      if (response.error) {
        setError(response.error);
        setIsSaving(false);
        return;
      }

      // The save went through, so fall back to what we sent if the response
      // isn't in a shape we can read.
      const savedLaunchpad = launchpadFromResponse(response.body) ?? (props.launchpad ? {
        ...props.launchpad,
        metadata: {
          ...props.launchpad.metadata,
          attributes
        }
      } : null);

      if (!savedLaunchpad) {
        setError('This Launchpad was saved, but the response could not be read. Please refresh.');
        setIsSaving(false);
        return;
      }

      props.onSaved(savedLaunchpad);
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    id='launchpad-view-modal'
    isOpen={props.isOpen}
    footerButtons={[
      {
        title: props.launchpad ? 'Save Changes' : 'Create Launchpad View',
        style: ModalButtonStyle.FILLED,
        enabled: !isSaving && description.trim().length > 0 && campusID.length > 0,
        onClick: saveLaunchpad
      },
      {
        title: 'Cancel',
        style: ModalButtonStyle.OUTLINE,
        enabled: !isSaving,
        onClick: props.close
      }
    ]}>
    <div className='launchpad-view-modal-content'>
      <div className='header'>
        <a aria-disabled={isSaving}
          className={isSaving ? 'close disabled' : 'close'}
          href='#'
          onClick={(e) => {
            e.preventDefault();

            if (isSaving) {
              return;
            }

            props.close();
          }}>
          <img className='icon' src={closeIcon} alt='Close icon' />
        </a>

        <h3 className='title'>{modalTitle}</h3>
      </div>

      {error ? <p className='save-error'>{error}</p> : null}

      <div className='body'>
        <div className='field-container'>
          <label htmlFor='launchpad-name'>Name</label>
          <input
            id='launchpad-name'
            type='text'
            name='launchpad-name'
            placeholder='Launchpad Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <p className='caption'>Who is this Launchpad for? We recommend including the campus name itself, as well as the category.</p>
        </div>

        <div className='field-container'>
          <label htmlFor='launchpad-description'>Description</label>
          <textarea
            id='launchpad-description'
            name='launchpad-description'
            rows={3}
            placeholder='Description'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <p className='caption'>A short summary of what people will find on this Launchpad.</p>
        </div>

        <div className='field-container'>
          <label htmlFor='launchpad-logo'>Logo</label>
          {existingLogoURL ? (
            <img src={existingLogoURL} alt='Current logo' className='logo-preview' />
          ) : null}
          <input
            id='launchpad-logo'
            type='file'
            name='launchpad-logo'
            accept='image/*'
            onChange={(e) => {
              setLogoFile(e.target.files?.[0] ?? null);
            }}
          />
          {logoFile ? <p className='caption'>Selected: {logoFile.name}</p> : null}
          <p className='caption'>Your logo image will be displayed in white or black at the top of Launchpad. For the best result, upload a logo with no background, where the logo touches the top and bottom edges of the frame.</p>
        </div>

        <div className='field-container'>
          <label htmlFor='launchpad-campus'>Campus</label>
          <select
            id='launchpad-campus'
            value={campusID}
            onChange={(e) => setCampusID(e.target.value)}>
            <option value='' disabled>Select a campus</option>
            {churchData.campuses.map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.attributes.name}
              </option>
            ))}
          </select>
          <p className='caption'>Launchpad will automatically display events, playlists, services, and more from the chosen campus.</p>
        </div>

        <div className='field-container'>
          <label htmlFor='launchpad-categories-trigger'>Category</label>
           <select
            id='launchpad-category'
            value={categoryID}
            onChange={(e) => setCategoryID(e.target.value)}>
            <option value=''>No category</option>
            {churchData.categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <p className='caption'>Selecting a category will filter what content is displayed on this Launchpad.</p>
        </div>
      </div>
    </div>
  </Modal>
}
